<template>
  <div class="bottom-dock-wrapper">
    <UiFloatingActionDock>
      <template #right>
        <div
          v-if="displayButtonRows && displayButtonRows.length > 0"
          class="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2"
        >
          <template v-for="(row, rIdx) in displayButtonRows" :key="rIdx">
            <UiButton
              v-for="(btn, bIdx) in row"
              :key="`${rIdx}_${bIdx}`"
              :variant="resolveVariant(btn.caption)"
              shape="pill"
              size="md"
              class="min-h-10 px-4 sm:px-5 text-sm font-semibold transition active:scale-95 shadow-sm"
              :disabled="isLoading"
              @click="$emit('button-click', btn)"
            >
              <i v-if="resolveIcon(btn.caption)" :class="resolveIcon(btn.caption)" class="mr-1.5"></i>
              <span>{{ btn.caption }}</span>
            </UiButton>
          </template>
        </div>

        <!-- Default fallback button if no buttons in state -->
        <div v-else class="flex items-center justify-center">
          <UiButton
            variant="primary"
            shape="pill"
            size="md"
            class="min-h-10 px-6 text-sm font-semibold transition active:scale-95"
            :disabled="isLoading"
            @click="$emit('button-click', { caption: 'Start Learning', callback_data: '/start' })"
          >
            <i class="fa-solid fa-play mr-2"></i>
            <span>Start Learning</span>
          </UiButton>
        </div>
      </template>
    </UiFloatingActionDock>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { UiFloatingActionDock, UiButton } from '../ui';
import type { OutboundButton } from '../types';

const props = defineProps<{
  buttons: OutboundButton[][];
  isLoading?: boolean;
}>();

defineEmits<{
  'button-click': [btn: OutboundButton];
}>();

const isInfoButton = (btn: OutboundButton): boolean => {
  const cap = (btn.caption || '').replace(/\ufe0f/g, '').trim().toLowerCase();
  const cb = (btn.callback_data || '').trim().toLowerCase();
  return (
    cap.includes('info') ||
    cap.includes('инфо') ||
    cap === 'ℹ' ||
    cb === '/info' ||
    cb.includes('learn words/info') ||
    cb.includes('courses/learn words/info')
  );
};

const isNextOrSkipButton = (btn: OutboundButton): boolean => {
  const cap = (btn.caption || '').replace(/\ufe0f/g, '').trim().toLowerCase();
  const cb = (btn.callback_data || '').trim().toLowerCase();
  return (
    cap.includes('next') ||
    cap.includes('skip') ||
    cap.includes('дальше') ||
    cap.includes('пропуск') ||
    cb === 'next' ||
    cb === 'skip' ||
    cb.includes('/next') ||
    cb.includes('/skip')
  );
};

const displayButtonRows = computed(() => {
  const filteredRows = (props.buttons || [])
    .map((row) => row.filter((btn) => !isInfoButton(btn)))
    .filter((row) => row.length > 0);

  if (filteredRows.length === 0) return [];

  // Flatten and partition so that Next / Skip buttons are positioned at the far right
  const allButtons = filteredRows.flat();
  const nextBtns = allButtons.filter(isNextOrSkipButton);
  const otherBtns = allButtons.filter((btn) => !isNextOrSkipButton(btn));

  return [[...otherBtns, ...nextBtns]];
});

const resolveVariant = (caption: string): 'primary' | 'secondary' | 'ghost' | 'danger' => {
  const lower = caption.toLowerCase();
  if (lower.includes('next') || lower.includes('start') || lower.includes('continue') || lower.includes('skip')) {
    return 'primary';
  }
  if (lower.includes('delete') || lower.includes('remove') || lower.includes('cancel')) {
    return 'danger';
  }
  return 'secondary';
};

const resolveIcon = (caption: string): string | null => {
  if (/\p{Extended_Pictographic}/u.test(caption)) {
    return null;
  }
  const lower = caption.toLowerCase();
  if (lower.includes('next') || lower.includes('skip')) return 'fa-solid fa-arrow-right';
  if (lower.includes('delete') || lower.includes('remove')) return 'fa-solid fa-trash';
  if (lower.includes('add word')) return 'fa-solid fa-plus';
  if (lower.includes('sound') || lower.includes('pronounce')) return 'fa-solid fa-volume-high';
  if (lower.includes('setting')) return 'fa-solid fa-gear';
  if (lower.includes('menu')) return 'fa-solid fa-bars';
  return null;
};
</script>

<style scoped>
.bottom-dock-wrapper :deep(.ui-floating-action-dock) {
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  justify-content: center !important;
  padding-left: 1rem !important;
  padding-right: 1rem !important;
  margin: 0 auto !important;
}

.bottom-dock-wrapper :deep(.ui-floating-action-dock__group) {
  margin: 0 auto !important;
}
</style>
